import { formatTo12Hour } from "@/lib/helper";
import type { BulkFacilityScheduleItem, MembershipFacilitySchedule } from "@/types/membership-facility-schedule";
// Parses a stored rrule_str into the RecurrenceConfig shape RecurrenceSelector expects.
// Mirrors the parser used by the facility schedule calendar.
export const parseRRuleToConfig = (rruleStr: string): Record<string, any> | undefined => {
  if (!rruleStr) return undefined;

  // Strip "RRULE:" prefix if present
  const raw = rruleStr.replace(/^RRULE:/i, "");

  // Parse key=value pairs
  const parts: Record<string, string> = {};
  raw.split(";").forEach((part) => {
    const [key, val] = part.split("=");
    if (key && val !== undefined) parts[key.toUpperCase()] = val;
  });

  const frequency = (parts["FREQ"] ?? "").toLowerCase(); // "weekly", "daily", etc.
  const interval = parts["INTERVAL"] ? parseInt(parts["INTERVAL"], 10) : 1;

  // BYDAY → array of day codes e.g. ["MO", "WE"]
  const mapRecurrenceDay = (dayCode: string, full: boolean = false) => {
    const normalized = dayCode.trim().toUpperCase();
    const code = normalized.replace(/^[-+]?\d+/, "");
    const map: Record<string, string> = {
      MO: full ? "Monday" : "Mon",
      TU: full ? "Tuesday" : "Tue",
      WE: full ? "Wednesday" : "Wed",
      TH: full ? "Thursday" : "Thu",
      FR: full ? "Friday" : "Fri",
      SA: full ? "Saturday" : "Sat",
      SU: full ? "Sunday" : "Sun",
    };
    return map[code] ?? dayCode.trim();
  };

  const weekDays = parts["BYDAY"] && frequency !== "monthly"
    ? parts["BYDAY"].split(",").map((d) => mapRecurrenceDay(d))
    : [];

  // Monthly specific parsing
  let monthlyType: "day" | "pattern" = "day";
  let monthlyDay: number = 1;
  let monthlyPattern: any = { week: "first", day: "Monday" };

  if (frequency === "monthly") {
    if (parts["BYMONTHDAY"]) {
      monthlyType = "day";
      monthlyDay = parseInt(parts["BYMONTHDAY"], 10);
    } else if (parts["BYDAY"]) {
      monthlyType = "pattern";
      const rawByDay = parts["BYDAY"];
      const match = rawByDay.match(/^([-+]?\d+)([A-Z]{2})$/);
      if (match) {
        const pos = match[1];
        const dayCode = match[2];
        const posMap: Record<string, string> = {
          "1": "first",
          "2": "second",
          "3": "third",
          "4": "fourth",
          "-1": "last",
        };
        monthlyPattern = {
          week: posMap[pos] || "first",
          day: mapRecurrenceDay(dayCode, true),
        };
      }
    }
  }

  let endCondition: string = "after";
  let endDate: string | undefined;
  let occurrences: number | undefined;

  if (parts["UNTIL"]) {
    endCondition = "on_date";
    const u = parts["UNTIL"].replace(/T.*$/, ""); // "20260507"
    endDate = `${u.substring(0, 4)}-${u.substring(4, 6)}-${u.substring(6, 8)}`;
  } else if (parts["COUNT"]) {
    endCondition = "after";
    occurrences = parseInt(parts["COUNT"], 10);
  }

  if (!frequency) return undefined;

  return {
    enabled: true,
    frequency,
    interval,
    weekDays,
    monthlyType,
    monthlyDay,
    monthlyPattern,
    endCondition,
    endDate,
    occurrences,
  };
};

export const toHHMM = (value?: string | null): string => (value ? value.substring(0, 5) : "");



/** Group flat window rows (one per window) into one item per facility. */
export const groupWindowsByFacility = (
  rows: MembershipFacilitySchedule[]
): BulkFacilityScheduleItem[] => {
  const map = new Map<number, BulkFacilityScheduleItem>();
  rows.forEach((row) => {
    const item = map.get(row.facility_id) ?? { facility_id: row.facility_id, windows: [] };
    item.windows.push({
      id: row.id,
      name: row.name,
      // Kept only as the recurrence anchor; windows have no validity dates.
      start_date: row.start_date,
      rrule_str: row.rrule_str,
      start_time: toHHMM(row.start_time),
      end_time: toHHMM(row.end_time),
    });
    map.set(row.facility_id, item);
  });
  return Array.from(map.values());
};

/**
 * End must be after start. An end of 00:00 (12:00 AM) means midnight, i.e. until the day
 * ends - the same rule the API applies.
 */
export const isValidHours = (start?: string | null, end?: string | null): boolean => {
  const from = toHHMM(start);
  const to = toHHMM(end);
  if (!from || !to) return false;
  return to > from || (to === "00:00" && from !== "00:00");
};

/** Returns an error message for the first invalid window, or null when all are valid. */
export const validateAvailabilityItems = (items: BulkFacilityScheduleItem[]): string | null => {
  for (const item of items) {
    for (const w of item.windows) {
      if (!w.start_time || !w.end_time) return "Each availability window needs start and end times.";
      if (!isValidHours(w.start_time, w.end_time)) return "Start time must be before end time.";
    }
  }
  return null;
};

export type CreditEligibility = "ok" | "blocked" | "unverified";

/**
 * Whether credits may be used for the facility slots in a POS cart.
 *
 * Each slot remembers which member its eligibility was worked out for
 * (`facility_slot_data.eligibilityMemberId`). "unverified" means a slot was added for someone
 * other than the member now being charged, so what the cart knows no longer applies and the
 * slot has to be added again for this member. The API enforces the rule in any case.
 */
export const creditEligibility = (items: unknown[], memberId?: number | null): CreditEligibility => {
  let result: CreditEligibility = "ok";
  for (const item of items) {
    const data = (item as any)?.facility_slot_data;
    if (!data?.slot) continue;
    if (data.slot.credit_eligible === false) return "blocked";
    if (memberId && data.eligibilityMemberId !== undefined && data.eligibilityMemberId !== memberId) {
      result = "unverified";
    }
  }
  return result;
};

/** True when a cart holds a slot the member's membership availability does not cover. */
export const hasCreditIneligibleSlot = (items: unknown[]): boolean =>
  creditEligibility(items) === "blocked";

export const CREDIT_UNVERIFIED_MESSAGE =
  "A facility slot in the cart was added for a different member. Remove it and add it again for this member to pay with credits.";

export const CREDIT_INELIGIBLE_MESSAGE =
  "Credits can only be used for slots within the member's membership availability.";

export type ComboboxOption = { label: string; value: string };

/**
 * Options for the shared searchable Combobox. It selects by label, so labels must be unique:
 * a repeated name gets its id appended.
 */
export const toComboboxOptions = (
  items: Array<{ id: number | string; name: string }>
): ComboboxOption[] => {
  const seen = new Map<string, number>();
  items.forEach((item) => {
    const key = item.name.trim().toLowerCase();
    seen.set(key, (seen.get(key) ?? 0) + 1);
  });
  return items.map((item) => ({
    value: String(item.id),
    label:
      (seen.get(item.name.trim().toLowerCase()) ?? 0) > 1 ? `${item.name} (#${item.id})` : item.name,
  }));
};

/** Value of the option with this label; cmdk may hand the label back lower-cased. */
export const findComboboxValue = (options: ComboboxOption[], label: string): string | null => {
  const wanted = (label ?? "").trim().toLowerCase();
  if (!wanted) return null;
  return options.find((o) => o.label.toLowerCase() === wanted)?.value ?? null;
};

export const comboboxLabel = (options: ComboboxOption[], value: string | number | null): string =>
  options.find((o) => o.value === String(value))?.label ?? "";

/** "17:00:00" -> "5:00 PM". */
export const formatTime12 = (value?: string | null): string => {
  const hhmm = toHHMM(value);
  return hhmm ? formatTo12Hour(hhmm) : "";
};

/**
 * 12-hour time range, with AM/PM written once when both ends share it:
 * "5:00 - 6:00 PM", "11:00 PM - 12:00 AM".
 */
export const formatTimeRange12 = (start?: string | null, end?: string | null): string => {
  const from = formatTime12(start);
  const to = formatTime12(end);
  if (!from || !to) return from || to;
  const [fromClock, fromPeriod] = from.split(" ");
  const toPeriod = to.split(" ")[1];
  return fromPeriod === toPeriod ? `${fromClock} - ${to}` : `${from} - ${to}`;
};
